<template>
  <div class="ticket-comments">
    <label v-if="title" class="section-label">{{ title }}</label>

    <!-- Scrollable comment list -->
    <div class="comment-scroll-area">
      <div v-if="comments.length <= 0" class="empty-state">
        <i class="tim-icons icon-chat-33"></i>
        <p>{{ emptyText }}</p>
      </div>

      <div v-for="c in comments" :key="'C' + c.id" class="comment-item"
           :class="{ 'comment-deleting': deletingId === c.id }">
        <div class="comment-row">
          <img v-if="c.img" :src="c.img" class="comment-avatar" alt="avatar" />
          <div v-else class="comment-avatar-initials">
            {{ getInitials(c.first_name, c.last_name) }}
          </div>
          <div class="comment-content">
            <div class="comment-meta">
              <span class="comment-author">{{ c.first_name }} {{ c.last_name }}</span>
              <span v-if="showCompany && c.company_name" class="comment-company">[{{ c.company_name }}]</span>
              <span class="comment-dot">&middot;</span>
              <span class="comment-time">{{ getLocalDateTime(c.created) }}</span>
            </div>
            <div class="comment-body" v-html="renderBody(c.comment)" @click="onBodyClick($event, c)"></div>
            <div v-if="deletingId === c.id" class="comment-deleting-indicator">
              <i class="el-icon-loading"></i> Deleting...
            </div>
            <div v-else-if="canComment && canEdit(c)" class="comment-actions">
              <a href="#" @click.prevent="startEdit(c)">Edit</a>
              <a href="#" @click.prevent="$emit('delete', c)">Delete</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add or edit comment -->
    <div v-if="canComment">
      <div v-if="!showEditor && !editingComment" style="padding: 8px 0;">
        <base-button size="sm" type="primary" @click="openEditor">
          <i class="tim-icons icon-simple-add mr-1"></i> Add comment
        </base-button>
      </div>

      <div v-if="showEditor || editingComment" class="new-comment-section">
        <label v-if="editingComment" class="section-label">Edit comment</label>
        <rich-text-editor
          ref="editor"
          v-model="commentHtml"
          toolbar="comment"
          placeholder="Add a comment..."
          height="auto"
          :dark="dark"
        />
        <div class="mt-2">
          <template v-if="!editingComment">
            <base-button size="sm" type="default" @click="reset" :disabled="saving">Cancel</base-button>
            <base-button size="sm" type="primary" class="ml-2" @click="submitCreate" :disabled="saving">
              <i v-if="saving" class="el-icon-loading mr-1"></i>{{ saving ? 'Saving...' : 'Comment' }}
            </base-button>
          </template>
          <template v-else>
            <base-button size="sm" type="default" @click="reset" :disabled="saving">Cancel</base-button>
            <base-button size="sm" type="primary" class="ml-2" @click="submitUpdate" :disabled="saving">
              <i v-if="saving" class="el-icon-loading mr-1"></i>{{ saving ? 'Saving...' : 'Update' }}
            </base-button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Comment thread for a ticket: list, hover Edit/Delete, and a collapsed
// composer. Persistence stays with the parent through events:
//   create(html)                     new comment
//   update({ comment, html })        edited comment
//   delete(comment)                  delete request
//   checklist-change({ comment, html, revert })   checkbox toggled inside a body
// After a successful create or update the parent calls `reset()` to close the
// composer. On failure it leaves the composer open so nothing is lost.
import RichTextEditor from '@/components/RichTextEditor'
import { sanitizeQuillHtml } from '@/util/quillSanitize'
import { stripDefaultTextColors } from '@/util/richText'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'

export default {
  name: 'ticket-comments',
  components: { RichTextEditor },
  props: {
    comments: { type: Array, default: () => [] },
    canComment: { type: Boolean, default: false },
    // Per-comment predicate for showing Edit and Delete.
    canEdit: { type: Function, default: () => true },
    saving: { type: Boolean, default: false },
    deletingId: { type: [Number, String], default: null },
    dark: { type: Boolean, default: true },
    title: { type: String, default: '' },
    emptyText: { type: String, default: 'Be the first one to comment.' },
    showCompany: { type: Boolean, default: false }
  },
  data() {
    return {
      showEditor: false,
      editingComment: null,
      commentHtml: ''
    }
  },
  methods: {
    renderBody(html) {
      return stripDefaultTextColors(sanitizeQuillHtml(html || ''))
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : ''
      const last = lastName ? lastName.charAt(0).toUpperCase() : ''
      return first + last
    },
    getLocalDateTime(d) {
      if (!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if (!date && !time) return ''
      return `${date}, ${time}`
    },
    openEditor() {
      this.showEditor = true
      this.commentHtml = ''
      this.$nextTick(() => {
        if (this.$refs.editor) this.$refs.editor.clear()
      })
    },
    startEdit(c) {
      this.editingComment = { ...c }
      this.commentHtml = c.comment
      this.$nextTick(() => {
        if (this.$refs.editor) this.$refs.editor.setContent(c.comment)
      })
    },
    editorContent() {
      return this.$refs.editor ? this.$refs.editor.getContent() : this.commentHtml
    },
    submitCreate() {
      this.$emit('create', this.editorContent())
    },
    submitUpdate() {
      this.$emit('update', { comment: this.editingComment, html: this.editorContent() })
    },
    // Closes the composer and clears the draft. Parent calls this after a save.
    reset() {
      this.commentHtml = ''
      if (this.$refs.editor) this.$refs.editor.clear()
      this.editingComment = null
      this.showEditor = false
    },
    onBodyClick(event, comment) {
      const target = event.target
      if (target.tagName !== 'INPUT' || target.type !== 'checkbox') return
      if (!this.canComment || !this.canEdit(comment)) {
        target.checked = !target.checked
        return
      }
      const bodyEl = target.closest('.comment-body')
      if (!bodyEl) return
      // Sync checkbox state into attributes so innerHTML carries it
      bodyEl.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        if (cb.checked) cb.setAttribute('checked', '')
        else cb.removeAttribute('checked')
      })
      const html = bodyEl.innerHTML
      const revert = () => { target.checked = !target.checked }
      this.$emit('checklist-change', { comment, html, revert })
    }
  }
}
</script>

<style scoped>
.section-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}
.empty-state i {
  font-size: 2.5em;
  color: rgba(255, 255, 255, 0.15);
}
.empty-state p {
  margin-top: 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

/* Scroll area */
.comment-scroll-area {
  max-height: 500px;
  overflow-y: auto;
}
.comment-scroll-area::-webkit-scrollbar {
  width: 6px;
}
.comment-scroll-area::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.02);
}
.comment-scroll-area::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

/* Rows */
.comment-item {
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.comment-item:last-child {
  border-bottom: none;
}
.comment-deleting {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.comment-deleting-indicator {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
}
.comment-row {
  display: flex;
  gap: 12px;
}
.comment-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.comment-avatar-initials {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #1d8cf8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
}
.comment-content {
  flex: 1;
  min-width: 0;
}
.comment-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
  flex-wrap: wrap;
}
.comment-author {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}
.comment-company {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.comment-dot {
  color: rgba(255, 255, 255, 0.3);
}
.comment-time {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
}

/* Body: rich text from the editor */
.comment-body {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  padding: 8px 10px;
  margin-top: 4px;
  word-break: break-word;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
}
/* Theme rules such as .card p paint text white or dark. Inherit from the
   surface instead. No !important, so inline palette colors still win. */
.comment-body ::v-deep * {
  color: inherit;
}
.comment-body ::v-deep p {
  margin-bottom: 4px;
}
.comment-body ::v-deep p:last-child {
  margin-bottom: 0;
}
.comment-body ::v-deep ol {
  list-style-type: decimal;
  padding-left: 2em;
}
.comment-body ::v-deep ul {
  list-style-type: disc;
  padding-left: 2em;
}
.comment-body ::v-deep a {
  color: #1d8cf8;
}
.comment-body ::v-deep .checklist-item {
  list-style: none !important;
  margin-left: -1.5em;
}
.comment-body ::v-deep .checklist-cb {
  appearance: none;
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(125, 125, 125, 0.4);
  border-radius: 3px;
  background: transparent;
  vertical-align: middle;
  position: relative;
  cursor: pointer;
  margin-right: 6px;
}
.comment-body ::v-deep .checklist-cb:checked {
  background-color: #e14eca;
  border-color: #e14eca;
}
.comment-body ::v-deep .checklist-cb:checked::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 0px;
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
}

/* Actions */
.comment-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.comment-item:hover .comment-actions {
  opacity: 1;
}
.comment-actions a {
  font-size: 12px;
  color: #e14eca;
  text-decoration: none;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background 0.15s ease;
}
.comment-actions a:hover {
  background: rgba(225, 78, 202, 0.1);
}
.new-comment-section {
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 8px;
}

/* Light mode */
.white-content .section-label {
  color: #525f7f;
}
.white-content .empty-state i {
  color: rgba(0, 0, 0, 0.15);
}
.white-content .empty-state p {
  color: #9A9A9A;
}
.white-content .comment-item {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}
.white-content .comment-deleting-indicator {
  color: #9A9A9A;
}
.white-content .comment-avatar {
  border-color: rgba(0, 0, 0, 0.1);
}
.white-content .comment-author {
  color: #344675;
}
.white-content .comment-company {
  color: #9A9A9A;
}
.white-content .comment-dot {
  color: #9A9A9A;
}
.white-content .comment-time {
  color: #9A9A9A;
}
.white-content .comment-body {
  color: #344675;
  background: rgba(0, 0, 0, 0.03);
}
.white-content .new-comment-section {
  border-top-color: rgba(0, 0, 0, 0.08);
}
.white-content .comment-scroll-area::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .comment-scroll-area::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
}
</style>
